import { useState } from "react";
import { OpenNowBadge, HoursTable } from "./OpenNow";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { InputField, SelectField, TextAreaField } from "@/components/ui/FormFields";
import { useAuth } from "@/context/AuthContext";
import ClaimButton from "@/components/ClaimButton";
import { MemberReachPanel } from './MemberReachPanel';
import { uploadDocument, openDocument, ALLOWED_DOCUMENT_TYPES } from "@/api/upload";
import { ProviderTrustFacts } from './ProviderTrustFacts';
import {
  getProvider,
  vouchForProvider,
  removeVouch,
  submitProviderVerification,
  getProviderVerifications,
  addProviderReference,
  removeProviderReference,
  followProvider,
  unfollowProvider,
  sendProviderMessage,
  type ProviderVouch,
  type ProviderVerificationType,
  type ProviderReferenceItem,
} from "@/api/providers";

// Friendly labels for the public verification badges.
const VERIFICATION_LABELS: Record<ProviderVerificationType, string> = {
  identity: "Identity verified",
  business_license: "Licensed business",
  professional_certification: "Certified professional",
  insurance: "Insured",
  background_check: "Background checked",
};

export default function ProviderDetail() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const qc = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  const [rating, setRating] = useState("5");
  const [comment, setComment] = useState("");
  const [occasion, setOccasion] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["provider", idOrSlug],
    queryFn: () => getProvider(idOrSlug!),
    enabled: !!idOrSlug,
  });

  const p = data?.data;

  async function handleVouch() {
    if (!p) return;
    setBusy(true);
    setFeedback(null);
    try {
      await vouchForProvider(p.id, {
        rating: Number(rating),
        comment: comment.trim() || undefined,
        occasion: occasion.trim() || undefined,
      });
      setComment("");
      setFeedback("Vouch added.");
      qc.invalidateQueries({ queryKey: ["provider", idOrSlug] });
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : "Failed to vouch");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveVouch() {
    if (!p) return;
    setBusy(true);
    setFeedback(null);
    try {
      await removeVouch(p.id);
      setFeedback("Vouch removed.");
      qc.invalidateQueries({ queryKey: ["provider", idOrSlug] });
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : "Failed to remove vouch");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (error)
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
        Failed to load provider.{" "}
        <button onClick={() => refetch()} className="underline font-medium">
          Retry
        </button>
      </div>
    );
  if (!p) return <p className="text-sm text-gray-500">Not found.</p>;

  return (
    <div className="space-y-6">
      <PageHeader
        title={p.name}
        subtitle={`${p.category.name}${p.subcategory ? ` · ${p.subcategory.name}` : ""} · ${p.city}, ${p.state}`}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Providers", href: "/lab/providers" },
          { label: p.name },
        ]}
        actions={
          user?.id && p.addedBy?.id === user.id ? (
            <Link to={`/lab/providers/${p.slug}/edit`}>
              <Button variant="secondary">Edit listing</Button>
            </Link>
          ) : undefined
        }
      />

      {feedback && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm px-3 py-2 rounded">
          {feedback}
        </div>
      )}

      <ReachOut providerId={p.id} providerName={p.name} />

      {/* Hours, price and reach: GET /providers/{id} hours, openNow, priceBand, serviceAreas */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 grid gap-4 sm:grid-cols-2">
        <div>
          <h2 className="font-semibold text-gray-900 mb-2">Hours</h2>
          <HoursTable hours={p.hours} />
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <h2 className="font-semibold text-gray-900">Price</h2>
            <p className={p.priceBand ? "text-gray-900" : "text-gray-400"}>{p.priceBand ?? "not stated"}</p>
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Travels to</h2>
            <p className={p.serviceAreas.length ? "text-gray-900" : "text-gray-400"}>
              {p.serviceAreas.length ? p.serviceAreas.join(", ") : "not stated"}
            </p>
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          {p.isVerified && <Badge variant="green">verified</Badge>}
          <Badge variant="gray">{p.category.name}</Badge>
          <OpenNowBadge openNow={p.openNow} />
        </div>
        {p.verifications.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {p.verifications.map((v) => (
              <span
                key={v.type}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-green-50 text-green-700 border border-green-200"
                title={v.verifiedAt ? `Verified ${new Date(v.verifiedAt).toLocaleDateString()}` : "Verified"}
              >
                ✓ {VERIFICATION_LABELS[v.type]}
              </span>
            ))}
          </div>
        )}
        <ProviderTrustFacts provider={p} />
        <dl className="grid grid-cols-3 gap-3 text-sm">
          <Stat label="Vouches" value={p.vouchCount} />
          <Stat label="Avg rating" value={p.avgRating > 0 ? p.avgRating.toFixed(1) : "—"} />
          <Stat label="Q&A mentions" value={p.mentionCount} />
        </dl>
        {p.description && (
          <p className="text-sm text-gray-700 mt-4">{p.description}</p>
        )}
        {/* Own photos and the name card are two fields; the card is never in `images`. */}
        <p className="text-xs text-gray-500 mt-3">
          {p.images.length} photo{p.images.length === 1 ? "" : "s"} of its own
          {p.nameCardUrl ? (
            <>
              {" · "}
              <a href={p.nameCardUrl} target="_blank" rel="noreferrer" className="underline">
                name card
              </a>
            </>
          ) : (
            " · no name card"
          )}
        </p>
        {p.languages.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-3">
            {p.languages.map((l) => (
              <Badge key={l} variant="gray">
                {l}
              </Badge>
            ))}
          </div>
        )}
        {p.dietFacts.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-3">
            {p.dietFacts.map((f) => (
              <Badge key={f} variant="green">
                {f}
              </Badge>
            ))}
          </div>
        )}
        {p.skills.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-500 mb-1.5">What they do</p>
            <div className="flex gap-1.5 flex-wrap">
              {p.skills.map((s) => (
                <span
                  key={s}
                  className="px-2.5 py-1 rounded-full text-xs bg-primary-50 text-primary-700 border border-primary-100"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
        {(p.phone || p.email || p.website) && (
          <div className="mt-4 text-sm text-gray-600 space-y-1">
            {p.phone && <div>📞 {p.phone}</div>}
            {p.email && <div>✉️ {p.email}</div>}
            {p.website && (
              <div>
                🔗{" "}
                <a
                  href={p.website}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  {p.website}
                </a>
              </div>
            )}
          </div>
        )}
      </section>

      {/* References — public social proof, owner-managed */}
      <ReferencesSection
        providerId={p.id}
        references={p.references}
        isOwner={!!user?.id && p.addedBy?.id === user.id}
        onChange={() => qc.invalidateQueries({ queryKey: ["provider", idOrSlug] })}
      />

      {/* Vouch */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Vouch for this provider</h2>
        {!isAuthenticated ? (
          <p className="text-sm text-gray-500">Log in to vouch.</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <SelectField
                label="Rating"
                value={rating}
                onChange={(e) => setRating(e.target.value)}
                options={[5, 4, 3, 2, 1].map((n) => ({
                  value: String(n),
                  label: `${n} ★`,
                }))}
              />
              <div className="md:col-span-3">
                <TextAreaField
                  label="Comment (optional)"
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Why do you recommend them?"
                />
              </div>
              <InputField label="Occasion (optional)" value={occasion} maxLength={60}
                onChange={(e) => setOccasion(e.target.value)} placeholder="Griha pravesh, Diwali, wedding…" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleVouch} isLoading={busy}>
                Add vouch
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleRemoveVouch}
                disabled={busy}
              >
                Remove my vouch
              </Button>
            </div>
          </div>
        )}
      </section>

      <MemberReachPanel providerId={p.id} signedIn={isAuthenticated} />

      {/* Claim — a real owner takes control of a seeded page */}
      {isAuthenticated && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Own this business?</h2>
          <ClaimButton entityType="provider" entityId={p.id} />
        </section>
      )}

      {/* Verification — owner-only server-side; the Lab always shows the
          form and surfaces the 403 so the gate itself is testable */}
      {isAuthenticated && <VerificationSection providerId={p.id} />}

      {/* Recent vouches */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Recent vouches
        </h2>
        {p.recentVouches.length === 0 ? (
          <p className="text-sm text-gray-500">No vouches yet — be the first.</p>
        ) : (
          <ul className="space-y-3">
            {p.recentVouches.map((v) => (
              <VouchRow key={v.id} v={v} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function VouchRow({ v }: { v: ProviderVouch }) {
  return (
    <li className="border border-gray-200 rounded-xl px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{v.user.name}</span>
        <Badge variant="yellow">{v.rating} ★</Badge>
        {v.occasion && <Badge variant="gray">{v.occasion}</Badge>}
        <span className="text-xs text-gray-400">
          {new Date(v.createdAt).toLocaleDateString()}
        </span>
      </div>
      {v.comment && <p className="text-sm text-gray-600 mt-1">{v.comment}</p>}
    </li>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function VerificationSection({ providerId }: { providerId: string }) {
  const qc = useQueryClient();
  const [vType, setVType] = useState<ProviderVerificationType>("business_license");
  // Uploaded through POST /upload/document: private, and only these
  // addresses are accepted by the verify call.
  const [docs, setDocs] = useState<{ url: string; name: string }[]>([]);
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["provider-verifications", providerId],
    queryFn: () => getProviderVerifications(providerId),
  });
  const verifications = data?.data ?? [];

  async function addDocument(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const doc = await uploadDocument(file);
      setDocs((d) => [...d, { url: doc.url, name: doc.fileName }]);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setMsg(null);
    try {
      await submitProviderVerification(providerId, {
        type: vType,
        documents: docs.map((d) => d.url),
        notes: notes || undefined,
      });
      setMsg("Submitted ✓ — an admin will review it");
      setDocs([]);
      setNotes("");
      qc.invalidateQueries({ queryKey: ["provider-verifications", providerId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed to submit");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Request verification</h2>
      <p className="text-xs text-gray-500 mb-3">
        Only the user who added or claimed this provider can submit (server-enforced).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SelectField
          label="Type"
          value={vType}
          onChange={(e) => setVType(e.target.value as ProviderVerificationType)}
          options={[
            { value: "business_license", label: "Business license" },
            { value: "identity", label: "Identity" },
            { value: "professional_certification", label: "Professional certification" },
            { value: "insurance", label: "Insurance" },
            { value: "background_check", label: "Background check" },
          ]}
        />
        <div className="md:col-span-2">
          <label className="label">Documents * (PDF, JPEG or PNG; kept private)</label>
          <input
            type="file"
            className="input"
            accept={ALLOWED_DOCUMENT_TYPES.join(",")}
            disabled={busy}
            onChange={addDocument}
          />
          {docs.map((d) => (
            <p key={d.url} className="text-xs text-gray-600 mt-1">
              {d.name}{" "}
              <button type="button" className="underline" onClick={() => openDocument(d.url).catch((err) => setMsg(err instanceof Error ? err.message : "Could not open"))}>
                open
              </button>
            </p>
          ))}
        </div>
      </div>
      <TextAreaField
        label="Notes (optional)"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Anything the reviewer should know"
      />
      <div className="flex items-center gap-3 mt-3">
        <Button size="sm" isLoading={busy} disabled={docs.length === 0} onClick={submit}>
          Submit for review
        </Button>
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>

      {verifications.length > 0 && (
        <div className="mt-4 space-y-1">
          <h3 className="text-sm font-medium text-gray-700">Requests</h3>
          {verifications.map((v) => (
            <p key={v.id} className="text-sm text-gray-600">
              {v.type} — <Badge variant={v.status === "approved" ? "green" : "gray"}>{v.status}</Badge>
              {v.reviewNotes ? ` · ${v.reviewNotes}` : ""}
              {(v.documents ?? []).map((url, i) => (
                <button key={url} type="button" className="ml-2 underline text-xs" onClick={() => openDocument(url).catch((err) => setMsg(err instanceof Error ? err.message : "Could not open"))}>
                  document {i + 1}
                </button>
              ))}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}

function ReferencesSection({
  providerId,
  references,
  isOwner,
  onChange,
}: {
  providerId: string;
  references: ProviderReferenceItem[];
  isOwner: boolean;
  onChange: () => void;
}) {
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Nothing to show a non-owner if there are no references yet.
  if (references.length === 0 && !isOwner) return null;

  async function add() {
    if (!name.trim() || !relationship.trim()) {
      setMsg("Name and relationship are required.");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await addProviderReference(providerId, {
        name: name.trim(),
        relationship: relationship.trim(),
        note: note.trim() || undefined,
      });
      setName("");
      setRelationship("");
      setNote("");
      onChange();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Couldn't add reference.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(refId: string) {
    setBusy(true);
    try {
      await removeProviderReference(providerId, refId);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">References</h2>
      <p className="text-xs text-gray-500 mb-4">
        People who’ll speak for this provider — from past clients and employers.
      </p>

      {references.length > 0 ? (
        <ul className="space-y-3">
          {references.map((r) => (
            <li
              key={r.id}
              className="flex items-start justify-between gap-3 border border-gray-100 rounded-xl p-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {r.name}{" "}
                  <span className="font-normal text-gray-500">· {r.relationship}</span>
                </p>
                {r.note && <p className="text-sm text-gray-600 mt-0.5">“{r.note}”</p>}
              </div>
              {isOwner && (
                <button
                  onClick={() => remove(r.id)}
                  disabled={busy}
                  className="text-xs text-gray-400 hover:text-red-600 shrink-0"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-400">No references yet.</p>
      )}

      {isOwner && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
          <p className="text-xs font-medium text-gray-500">Add a reference</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InputField
              label="Name"
              placeholder="Anjali Mehta"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <InputField
              label="Relationship"
              placeholder="Employer (2020–2023)"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
            />
          </div>
          <TextAreaField
            label="What they say"
            rows={2}
            placeholder="Cared for our twins for three years — completely trustworthy."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {msg && <p className="text-xs text-red-500">{msg}</p>}
          <Button onClick={add} isLoading={busy} size="sm">
            Add reference
          </Button>
        </div>
      )}
    </section>
  );
}


/**
 * The two things a member does TO a business, and they are not the same thing.
 *
 * FOLLOWING is quiet and ongoing: it says "tell me when this shop posts". It
 * costs the business nothing and creates no obligation.
 *
 * MESSAGING opens a thread, and on the shop's desk that thread arrives as a
 * LEAD — the number the whole owner dashboard is built around. So the button
 * says what it does rather than "contact": somebody is about to appear in
 * another person's inbox expecting an answer.
 */
function ReachOut({ providerId, providerName }: { providerId: string; providerName: string }) {
  const { isAuthenticated } = useAuth();
  const [following, setFollowing] = useState<boolean | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!isAuthenticated) return null;

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      await fn();
      setNote(ok);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-600">
          {following === null
            ? "Follow to hear when they post"
            : following
              ? "Following — their updates reach you"
              : "Not following"}
        </span>
        <Button
          size="sm"
          variant={following ? "primary" : "secondary"}
          isLoading={busy}
          onClick={() =>
            run(async () => {
              const res = following
                ? await unfollowProvider(providerId)
                : await followProvider(providerId);
              setFollowing(res.data.following);
            }, following ? "Unfollowed." : "Following.")
          }
        >
          {following ? "Following ✓" : "Follow"}
        </Button>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-900">Ask them something</p>
        <p className="text-xs text-gray-500 mb-2">
          This opens a thread, and it lands on their desk as a <strong>lead</strong>{" "}
          — the number that dashboard is built around. Somebody will be
          expecting an answer.
        </p>
        <TextAreaField
          label=""
          rows={2}
          placeholder={`Do you cater for forty people?`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="mt-2">
          <Button
            size="sm"
            isLoading={busy}
            disabled={!body.trim()}
            onClick={() =>
              run(async () => {
                await sendProviderMessage(providerId, body.trim(), crypto.randomUUID());
                setBody("");
              }, `Sent to ${providerName}. It is now a lead on their desk.`)
            }
          >
            Send
          </Button>
        </div>
      </div>

      {note && <p className="text-sm text-green-700">{note}</p>}
      {err && <p className="text-sm text-red-700">{err}</p>}
    </section>
  );
}
